// Sprint P0.1 · UnreadContext — polling partagé toutes les 30 s.
//
// Une seule boucle pour toute l'app. Alimente le badge sidebar Messages,
// se rafraîchit sur focus onglet + à la demande (après mark-read / envoi).
//
// Consomme le seul endpoint `/api/dashboard/summary.totals`.

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const POLL_MS = 30_000;

const UnreadContext = createContext({
  conversationsWithUnread: 0,
  awaitingReply: 0,
  unreadMessages: 0,
  refresh: () => {},
});

export const UnreadProvider = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState({
    conversationsWithUnread: 0,
    awaitingReply: 0,
    unreadMessages: 0,
  });
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!user || inFlight.current) return;
    inFlight.current = true;
    try {
      const data = await api.getDashboardSummary();
      const t = data?.totals || {};
      setState({
        conversationsWithUnread: t.conversations_with_unread || 0,
        awaitingReply:           t.awaiting_reply           || 0,
        unreadMessages:          t.unread_messages          || 0,
      });
    } catch {
      // silent — polling léger, pas de toast
    } finally {
      inFlight.current = false;
    }
  }, [user]);

  // Fetch initial + polling 30 s
  useEffect(() => {
    if (!user) return;
    refresh();
    const iv = setInterval(refresh, POLL_MS);
    return () => clearInterval(iv);
  }, [user, refresh]);

  // Refresh au retour de focus sur l'onglet
  useEffect(() => {
    if (!user) return;
    const onFocus = () => refresh();
    const onVisibility = () => { if (!document.hidden) refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, refresh]);

  return (
    <UnreadContext.Provider value={{ ...state, refresh }}>
      {children}
    </UnreadContext.Provider>
  );
};

export const useUnread = () => useContext(UnreadContext);

export default UnreadContext;
